/**
 * comparisonAttachmentStore
 *
 * Owns the "which uploaded file is selected for comparison" state, keyed per
 * chat session. The selection used to be a single pane-global variable, which
 * leaked across MS Project documents when the pane re-bound to another project
 * (issue 02/07: uploading a file in project A left it attached to project B's
 * chat after a window switch, silently comparing the wrong pair).
 *
 * Keying:
 *   - a real session id once the chat session exists;
 *   - "pending:<projectGuid>" for an upload made before the first message
 *     (sessions are created lazily on first send), migrated onto the real
 *     session id at creation;
 *   - "pane" only when neither identity is known yet (pre-lock degenerate
 *     state).
 */

export function attachmentKeyFor(sessionId, projectGuid) {
  if (sessionId) return sessionId;
  if (projectGuid) return `pending:${projectGuid}`;
  return "pane";
}

export class ComparisonAttachmentStore {
  constructor() {
    this._byKey = new Map();
  }

  get(key) {
    const attachment = this._byKey.get(key);
    return attachment ? { ...attachment } : null;
  }

  set(key, attachment) {
    if (attachment) {
      this._byKey.set(key, { ...attachment });
    } else {
      this._byKey.delete(key);
    }
  }

  clear(key) {
    this._byKey.delete(key);
  }

  clearAll() {
    this._byKey.clear();
  }

  /**
   * Move a pending (pre-session) selection onto the real session id once the
   * session is created. Never clobbers a selection already made under the
   * target key; always drops the pending entry so it cannot resurface in a
   * later "new chat" of the same project.
   */
  migrate(fromKey, toKey) {
    if (fromKey === toKey) return;
    const attachment = this._byKey.get(fromKey);
    if (attachment && !this._byKey.has(toKey)) {
      this._byKey.set(toKey, attachment);
    }
    this._byKey.delete(fromKey);
  }

  /**
   * Seed a selection hydrated from the backend session record (survives pane
   * reloads). A live local selection wins over the server copy: the local one
   * is newer by construction (every local change is synced back).
   */
  seed(key, attachment) {
    if (!attachment || !attachment.file_id || this._byKey.has(key)) return;
    this._byKey.set(key, { ...attachment });
  }
}
